<script setup lang="ts">
import type { AuctionItemEntity, AuctionListingItemEntity } from '~/apiClient';

type ReservePriceNotice = 'notSet' | 'reached' | 'notReached';

const RESERVE_PRICE_NOTICE_TO_TEXT_COLOR_MAP: Record<ReservePriceNotice, string> = {
    notSet: 'text-info-600',
    reached: 'text-success-600',
    notReached: 'text-warning-600',
};

// PROPS
interface Props {
    auction: AuctionItemEntity | AuctionListingItemEntity;
}

const { auction } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();

// DATA
const hasBids = computed(() => {
    if (!isLoggedIn.value) return false;

    const isDetailItem = (a: AuctionItemEntity | AuctionListingItemEntity): a is AuctionItemEntity => !!a.userMeta;

    if (isDetailItem(auction)) return !!(auction.userMeta?.maxBidder || auction.userMeta?.outBid);

    return !!auction.bidInfo;
});
const hasNoReservePriceNotice = computed(() => !auction.reservePriceSet);
const hasReserveReachedNotice = computed(() => !!auction.reservePriceReached);
const hasReserveNotReachedNotice = computed(() => hasBids.value && auction.reservePriceReached === false);
const notice = computed<ReservePriceNotice | undefined>(() => {
    if (hasNoReservePriceNotice.value) {
        return 'notSet';
    } else if (hasReserveReachedNotice.value) {
        return 'reached';
    } else if (hasReserveNotReachedNotice.value) {
        return 'notReached';
    }
});
const iconColor = computed(() => (notice.value ? RESERVE_PRICE_NOTICE_TO_TEXT_COLOR_MAP[notice.value] : ''));
</script>

<template>
    <BaseTooltip v-if="!!notice" :text="tt(`auction.reservePrice.tooltip.${notice}`)">
        <span class="flex items-center gap-0.5 rounded-full border border-solid border-neutral-400 bg-white px-2 py-1 text-sm">
            <UIcon class="text-base" name="lucide:shield-alert" :class="iconColor" />

            <BaseI18nT tag="span" class="text-highlighted" :keypath="`auction.reservePrice.${notice}.base`">
                <span class="font-semibold">
                    {{ tt(`auction.reservePrice.${notice}.highlight`) }}
                </span>
            </BaseI18nT>

            <UIcon class="text-muted" name="lucide:info" />
        </span>
    </BaseTooltip>
</template>
